// src/pages/StoreCatalogPanel.jsx
//
// PRODUCT CATALOG — the shared manufacturer catalog, per store.
//
// A store's catalog is no longer a private copy of products. It is:
//   • the shared catalog (catalog_products: every brand, one row per piece,
//     photo + specs) filtered to the brands the store carries (store_brands,
//     assigned by Exitium — a brand is a dealer agreement, so stores see it
//     read-only), plus
//   • the store's prices where a feed supplies them (store_product_prices;
//     East Bay's come from WonderSign). No price editor on purpose: a product
//     with no price on file is still fully known to the phone agent, which
//     just won't quote a number for it.
//   • the store's own one-off items (agent_catalog_items) — the upload lane
//     that was the whole tab before, now the "Your own items" view. The
//     parent passes that component in as `ownItems`.
//
// This panel is read-only: browse, search by name or item number, filter by
// brand. Reads go straight to Supabase under the signed-in user's RLS
// (catalog_products: any signed-in user; store_brands / store_product_prices:
// the store's owner).

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  Package,
  Upload,
  Lock,
} from "lucide-react";
import { supabase } from "../supabaseClient";

const PAGE_SIZE = 50;

// Brand key (catalog_products.brand / store_brands.brand) -> label. Keep in
// step with SHARED_CATALOG_BRANDS in ItemAutocomplete.jsx and
// CATALOG_BRAND_SOURCES in api/generate-flyer.py.
const BRAND_LABELS = {
  ashley: "Ashley",
  acme: "ACME",
  flexsteel: "Flexsteel",
  newclassic: "New Classic",
  coaster: "Coaster",
  eastbay: "East Bay",
};
// Brands offered on the platform, in display order. `eastbay` is one
// store's own line, so it only shows when that store has it on.
const PLATFORM_BRANDS = ["ashley", "acme", "flexsteel", "newclassic", "coaster"];
const brandLabel = (key) => BRAND_LABELS[key] || key;

const BRAND_CHIP = {
  ashley: "bg-blue-50 text-blue-800 border-blue-200",
  acme: "bg-violet-50 text-violet-800 border-violet-200",
  flexsteel: "bg-orange-50 text-orange-800 border-orange-200",
  newclassic: "bg-teal-50 text-teal-800 border-teal-200",
  coaster: "bg-amber-50 text-amber-800 border-amber-200",
  eastbay: "bg-slate-100 text-slate-700 border-slate-200",
};

const money = (n) =>
  n == null ? null : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// PostgREST `or=(...)` filter: the query must not carry its own delimiters.
// Spaces become wildcards so "tamarack dresser" matches "Tamarack - Dresser".
const searchPattern = (q) => {
  const safe = q.trim().replace(/[,()\\%*]/g, " ").replace(/\s+/g, " ").trim();
  return safe ? `*${safe.replace(/ /g, "*")}*` : "";
};

const storeIdOf = (s) => s?.id ?? s?.store_id ?? null;

// Mirrors DEFAULT_ADMIN_EMAILS in api/phone-agents-2/store-brands.py.
const PLATFORM_ADMIN_EMAILS = ["byars.jp5@gmail.com"];

const StoreCatalogPanel = ({ currentStore, ownItems = null }) => {
  const storeId = storeIdOf(currentStore);

  const [view, setView] = useState("shared"); // shared | own
  // Exitium staff see a Manage link to /admin/brands. The API is the real
  // gate (by login email); this only decides whether to show the link.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const email = (data?.session?.user?.email || "").toLowerCase();
      setIsAdmin(PLATFORM_ADMIN_EMAILS.includes(email));
    });
  }, []);

  // ---- brands + counts + prices (loaded once per store) ----
  const [brands, setBrands] = useState(null); // [{brand, enabled}] or null while loading
  const [counts, setCounts] = useState({}); // brand -> product count
  const [prices, setPrices] = useState(() => new Map()); // sku -> price
  const [setupError, setSetupError] = useState(null);

  // ---- browse state ----
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const requestSeq = useRef(0);

  const enabledBrands = useMemo(
    () => (brands || []).filter((b) => b.enabled).map((b) => b.brand),
    [brands],
  );

  const loadSetup = useCallback(async () => {
    if (!storeId) return;
    setSetupError(null);
    try {
      const { data: brandRows, error: brandErr } = await supabase
        .from("store_brands")
        .select("brand,enabled")
        .eq("store_id", storeId);
      if (brandErr) throw brandErr;
      const enabled = (brandRows || []).filter((b) => b.enabled).map((b) => b.brand);
      setBrands(brandRows || []);

      // Product count per carried brand — one counted single-row GET each,
      // in parallel (a GET rather than a HEAD: same count, works everywhere).
      const countPairs = await Promise.all(
        enabled.map(async (brand) => {
          const { count, error } = await supabase
            .from("catalog_products")
            .select("sku", { count: "exact" })
            .eq("brand", brand)
            .eq("is_active", true)
            .limit(1);
          return [brand, error ? null : count || 0];
        }),
      );
      setCounts(Object.fromEntries(countPairs));

      // The store's prices (only where a feed supplied one). Paged past the
      // 1000-row cap; East Bay has ~13k.
      const map = new Map();
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase
          .from("store_product_prices")
          .select("sku,price")
          .eq("store_id", storeId)
          .order("sku")
          .range(from, from + 999);
        if (error) throw error;
        for (const r of data || []) map.set(r.sku, r.price);
        if (!data || data.length < 1000) break;
      }
      setPrices(map);
    } catch (e) {
      setSetupError(e?.message || "Could not load the store's catalog settings");
      setBrands((b) => b || []);
    }
  }, [storeId]);

  useEffect(() => {
    setBrands(null);
    setCounts({});
    setPrices(new Map());
    setPage(0);
    loadSetup();
  }, [loadSetup]);

  // debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(0);
  }, [debouncedQ, brandFilter]);

  // ---- the page of products ----
  const loadPage = useCallback(async () => {
    if (brands === null) return; // wait for the brand list
    const wanted = brandFilter === "all" ? enabledBrands : enabledBrands.filter((b) => b === brandFilter);
    if (wanted.length === 0) {
      setRows([]);
      setTotal(0);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      let query = supabase
        .from("catalog_products")
        .select("sku,brand,item_no,name,room,type,is_set,piece_count,thumb:data->thumbs->>0", { count: "exact" })
        .in("brand", wanted)
        .eq("is_active", true);
      const pat = searchPattern(debouncedQ);
      if (pat) query = query.or(`name.ilike.${pat},item_no.ilike.${pat}`);
      const from = page * PAGE_SIZE;
      const { data, count, error } = await query.order("sku").range(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      if (seq !== requestSeq.current) return; // a newer request superseded this one
      setRows(data || []);
      setTotal(count || 0);
    } catch (e) {
      if (seq === requestSeq.current) setLoadError(e?.message || "Could not load products");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [brands, enabledBrands, brandFilter, debouncedQ, page]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const totalProducts = useMemo(
    () => enabledBrands.reduce((n, b) => n + (counts[b] || 0), 0),
    [enabledBrands, counts],
  );
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const shownBrands = useMemo(() => {
    const extra = (brands || []).map((b) => b.brand).filter((b) => !PLATFORM_BRANDS.includes(b));
    return [...PLATFORM_BRANDS, ...extra];
  }, [brands]);
  const isEnabled = (brand) => enabledBrands.includes(brand);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* ---------- header ---------- */}
      <div className="flex-shrink-0 px-4 sm:px-5 pt-4 pb-3 flex items-center justify-between gap-3">
        <h3 className="text-lg font-bold text-slate-900">Product Catalog</h3>
        <div className="inline-flex p-0.5 bg-slate-100 border border-slate-200 rounded-lg text-[12px] font-semibold">
          <button
            type="button"
            onClick={() => setView("shared")}
            data-tip="Every product in the brands this store carries"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
              view === "shared" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Package className="w-3.5 h-3.5" />
            Catalog
          </button>
          <button
            type="button"
            onClick={() => setView("own")}
            data-tip="Products you upload yourself — anything not in a manufacturer catalog"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
              view === "own" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            Your own items
          </button>
        </div>
      </div>

      {view === "own" ? (
        <div className="flex-1 min-h-0 flex flex-col">{ownItems}</div>
      ) : (
        <>
          {/* ---------- brands you carry ---------- */}
          <div className="flex-shrink-0 px-4 sm:px-5 pb-3">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Brands you carry</p>
              <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-400">
                <Lock className="w-3 h-3" />
                Set by Exitium — ask us to add a brand
                {isAdmin && (
                  <a href="/admin/brands" className="ml-1 text-blue-600 hover:underline font-semibold">Manage</a>
                )}
              </span>
            </div>
            {brands === null ? (
              <div className="flex items-center gap-2 text-[12px] text-slate-400">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Loading…
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {shownBrands.map((brand) => {
                  const on = isEnabled(brand);
                  if (!on && brand === "eastbay") return null;
                  return (
                    <span
                      key={brand}
                      className={`inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1 rounded-full border text-[12px] font-semibold ${
                        on ? BRAND_CHIP[brand] || "bg-slate-100 text-slate-700 border-slate-200" : "bg-white text-slate-400 border-slate-200 border-dashed"
                      }`}
                    >
                      {brandLabel(brand)}
                      <span className={`text-[10.5px] font-medium tabular-nums ${on ? "opacity-70" : ""}`}>
                        {on ? (counts[brand] == null ? "…" : counts[brand].toLocaleString()) : "not carried"}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* ---------- count line ---------- */}
          {brands !== null && enabledBrands.length > 0 && (
            <div className="flex-shrink-0 px-4 sm:px-5 pb-3 flex items-center gap-2 flex-wrap text-[13px] text-slate-600">
              <span className="tabular-nums">
                {totalProducts.toLocaleString()} products your agent knows
                {prices.size > 0 ? ` · ${prices.size.toLocaleString()} with a price on file` : " · no prices on file"}
              </span>
              <button
                type="button"
                onClick={() => { loadSetup(); loadPage(); }}
                title="Refresh"
                className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          )}

          {/* ---------- search + brand filter ---------- */}
          {brands !== null && enabledBrands.length > 0 && (
            <div className="flex-shrink-0 px-4 sm:px-5 pb-3 flex items-center gap-2">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or item number…"
                  className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-base sm:text-[13px] focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
                />
              </div>
              {enabledBrands.length > 1 && (
                <div className="relative flex-shrink-0">
                  <select
                    value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    className="appearance-none pl-3 pr-8 py-2 rounded-xl border border-slate-200 text-[13px] bg-white focus:outline-none focus:border-amber-400 transition-all max-w-[180px]"
                  >
                    <option value="all">All brands</option>
                    {enabledBrands.map((b) => (
                      <option key={b} value={b}>{brandLabel(b)}</option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              )}
            </div>
          )}

          {/* ---------- errors ---------- */}
          {(setupError || loadError) && (
            <div className="flex-shrink-0 mx-4 sm:mx-5 mb-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12px] flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{setupError || loadError}</span>
            </div>
          )}

          {/* ---------- list ---------- */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-5 pb-4">
            {brands !== null && enabledBrands.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center">
                <Package className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                <p className="text-[13px] font-semibold text-slate-700">No manufacturer catalog assigned yet</p>
                <p className="text-[12px] text-slate-500 mt-1 max-w-sm mx-auto">
                  Ask us to turn on the brands you carry and your agent will know every product in them.
                  Products you upload under <span className="font-semibold">Your own items</span> work either way.
                </p>
              </div>
            ) : rows.length === 0 && !loading && brands !== null ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-[13px] text-slate-500">
                {debouncedQ.trim() ? `Nothing matches “${debouncedQ.trim()}”.` : "No products."}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white overflow-hidden">
                {rows.map((it) => {
                  const price = prices.get(it.sku);
                  return (
                    <li key={it.sku} className="flex items-center gap-3 px-3 py-2">
                      {it.thumb ? (
                        <img src={it.thumb} alt="" loading="lazy" className="w-10 h-10 rounded-lg object-cover bg-slate-100 flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] text-slate-900 truncate">{it.name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <span className="text-[11px] font-mono text-slate-500">{it.item_no}</span>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${BRAND_CHIP[it.brand] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                            {brandLabel(it.brand)}
                          </span>
                          {it.is_set && (it.piece_count || 1) > 1 && (
                            <span className="text-[10px] text-slate-500">{it.piece_count}-piece set</span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        {price != null ? (
                          <span className="text-[13px] font-semibold text-slate-900 tabular-nums">{money(price)}</span>
                        ) : (
                          <span className="text-[11px] text-slate-400">No price on file</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ---------- pager ---------- */}
          {brands !== null && enabledBrands.length > 0 && total > 0 && (
            <div className="flex-shrink-0 px-4 sm:px-5 py-2.5 border-t border-slate-200 bg-white flex items-center justify-between gap-3 text-[12px] text-slate-500">
              <span className="tabular-nums">
                {(page * PAGE_SIZE + 1).toLocaleString()}–{Math.min(total, (page + 1) * PAGE_SIZE).toLocaleString()} of {total.toLocaleString()}
                {debouncedQ.trim() || brandFilter !== "all" ? " matching" : ""}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  aria-label="Previous page"
                  className="w-7 h-7 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="tabular-nums px-1">{page + 1} / {pageCount}</span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1 || loading}
                  aria-label="Next page"
                  className="w-7 h-7 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StoreCatalogPanel;
